import { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Box, Breadcrumbs, Button, Link as MuiLink, Typography } from '@mui/material'
import { Add as AddIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { AssignmentsTable } from '@/components/programs/AssignmentsTable'
import { AssignProgramDialog } from '@/components/programs/AssignProgramDialog'
import { useUserStore } from '@/stores/userStore'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'

/**
 * Программы конкретного ученика — пункт левого меню в контексте ученика
 * (/users/:id/programs).
 *
 * Отдельной страницей, а не блоком профиля: назначений у ученика бывает
 * несколько, им нужна пагинация и место под строку, а профиль отвечает на
 * другой вопрос — кто это.
 */
export function UserProgramsPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const userId = id ? parseInt(id, 10) : NaN

  const [assignOpen, setAssignOpen] = useState(false)
  const [assignedToken, setAssignedToken] = useState(0)

  const currentUser = useUserStore((state) => state.currentUser)
  const fetchUser = useUserStore((state) => state.fetchUser)

  useEffect(() => {
    if (Number.isNaN(userId)) return
    fetchUser(userId).catch(() => {})
  }, [userId, fetchUser])

  const isLoaded = currentUser !== null && currentUser.id === userId

  const userName = isLoaded
    ? [currentUser.surname, currentUser.first_name].filter(Boolean).join(' ').trim() || currentUser.email
    : undefined

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <MuiLink
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('users.title')}
          </MuiLink>
          {userName && (
            <MuiLink component={RouterLink} to={`${ROUTES.USERS}/${userId}`} underline="hover">
              {userName}
            </MuiLink>
          )}
          <Typography color="text.primary">{t('programs.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('programs.title')}
          </Typography>
          <PageHelpPopover pageKey="user-programs" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ml: 'auto' }}
            disabled={Number.isNaN(userId)}
            onClick={() => setAssignOpen(true)}
          >
            {t('programs.assign')}
          </Button>
        </Box>

        {!Number.isNaN(userId) && (
          <AssignmentsTable
            // Пересобирает таблицу после назначения: она грузит список сама
            key={assignedToken}
            studentId={userId}
            emptyText={t('programs.assignments.emptyForStudent')}
          />
        )}

        {!Number.isNaN(userId) && (
          <AssignProgramDialog
            open={assignOpen}
            studentId={userId}
            onClose={() => setAssignOpen(false)}
            onAssigned={() => setAssignedToken((value) => value + 1)}
          />
        )}
      </Box>
    </DashboardLayout>
  )
}
